import { useTranslation } from 'react-i18next';
import { Link } from 'wouter';
import { User, Users } from 'lucide-react';
import { Label } from '@/components/ui/label';
import { ModeCard, ContextualHint } from '../components';

export type AvailabilityServiceType = 'one_customer' | 'group';

interface ServiceTypeStepProps {
  value: AvailabilityServiceType | null;
  onChange: (kind: AvailabilityServiceType) => void;
  // When false, the group card renders disabled with an explanatory hint.
  // Defaults to true so existing callers keep working.
  groupEnabled?: boolean;
  groupDisabledHint?: string;
  // Optional way out when group is disabled for lack of group services.
  groupDisabledLink?: { href: string; label: string };
}

// Step 0 of every availability flow: choose between an open one-customer
// availability window and a fixed-time group class. The choice routes the
// wizard down two distinct pipelines downstream.
export function ServiceTypeStep({
  value,
  onChange,
  groupEnabled = true,
  groupDisabledHint,
  groupDisabledLink,
}: ServiceTypeStepProps) {
  const { t } = useTranslation('staff');

  return (
    <div className="p-6 space-y-4">
      <div className="space-y-1">
        <Label variant="section">
          {t('availability.serviceType.title', 'Service type')}
        </Label>
        <p className="text-sm text-gray-600">
          {t(
            'availability.serviceType.description',
            'Choose what kind of availability you want to create.'
          )}
        </p>
      </div>

      <ModeCard
        icon={User}
        title={t('availability.serviceType.oneCustomer.title', 'One-customer service')}
        description={t(
          'availability.serviceType.oneCustomer.desc',
          'Open booking window. Customers pick any available slot inside the window for a 1-on-1 session.'
        )}
        selected={value === 'one_customer'}
        onSelect={() => onChange('one_customer')}
      />

      {groupEnabled ? (
        <ModeCard
          icon={Users}
          title={t('availability.serviceType.group.title', 'Group service')}
          description={t(
            'availability.serviceType.group.desc',
            'Fixed-time class for many people. Pick a start time; end time is the service duration. Capacity comes from the service.'
          )}
          selected={value === 'group'}
          onSelect={() => onChange('group')}
          theme="purple"
        />
      ) : (
        <div className="space-y-2">
          {/* Shown greyed out so people know group classes exist. */}
          <div aria-disabled="true" className="pointer-events-none select-none opacity-50">
            <ModeCard
              icon={Users}
              title={t('availability.serviceType.group.title', 'Group service')}
              description={t(
                'availability.serviceType.group.desc',
                'Fixed-time class for many people. Pick a start time; end time is the service duration. Capacity comes from the service.'
              )}
              selected={false}
              onSelect={() => {}}
              theme="purple"
            />
          </div>
          <ContextualHint variant="info">
            {groupDisabledHint ??
              t(
                'availability.serviceType.groupComingSoon',
                'Group classes are coming soon to this view.'
              )}
            {groupDisabledLink && (
              <>
                {' '}
                <Link href={groupDisabledLink.href} className="font-semibold text-brand-700 underline hover:text-brand-900">
                  {groupDisabledLink.label}
                </Link>
              </>
            )}
          </ContextualHint>
        </div>
      )}
    </div>
  );
}
